import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';

export const RoleSchema = z.enum([
  'platform_administrator',
  'tenant_administrator',
  'site_administrator',
  'presenter',
]);
export type Role = z.infer<typeof RoleSchema>;

export const AccessModeSchema = z.enum(['public_code', 'private_link', 'code_pin']);
export type AccessMode = z.infer<typeof AccessModeSchema>;

export const SessionStatusSchema = z.enum(['starting', 'live', 'stopping', 'ended', 'failed']);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const QuestionStatusSchema = z.enum(['new', 'answered', 'dismissed']);
export type QuestionStatus = z.infer<typeof QuestionStatusSchema>;

export const QualityProfileSchema = z.enum(['economy', 'standard', 'quality']);
export type QualityProfile = z.infer<typeof QualityProfileSchema>;

export const ConnectionQualitySchema = z.enum(['excellent', 'good', 'poor']);
export type ConnectionQuality = z.infer<typeof ConnectionQualitySchema>;

export const VideoProfiles = {
  economy: { width: 1280, height: 720, fps: 30, bitrateKbps: 1500 },
  standard: { width: 1920, height: 1080, fps: 30, bitrateKbps: 3000 },
  quality: { width: 1920, height: 1080, fps: 30, bitrateKbps: 5000 },
} as const satisfies Record<QualityProfile, VideoProfile>;

export interface VideoProfile {
  width: number;
  height: number;
  fps: number;
  bitrateKbps: number;
}

export const BrandProfileSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  displayName: z.string().trim().min(1).max(100),
  logoUrl: z.url().optional(),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  backgroundImageUrl: z.url().optional(),
  welcomeText: z.string().trim().max(500).optional(),
  supportText: z.string().trim().max(300).optional(),
  accessMode: AccessModeSchema.default('public_code'),
  questionsEnabled: z.boolean().default(true),
});
export type BrandProfile = z.infer<typeof BrandProfileSchema>;

export const DeviceConfigurationSchema = z.object({
  brandProfileId: z.uuid(),
  displayId: z.string().min(1).max(255).optional(),
  microphoneId: z.string().min(1).max(255).optional(),
  qualityProfile: QualityProfileSchema.default('standard'),
  hideObs: z.boolean().default(true),
  autoStart: z.boolean().default(true),
  displayJoinCode: z.boolean().default(true),
  questionsEnabled: z.boolean().default(true),
  accessMode: AccessModeSchema.default('public_code'),
  maximumSessionMinutes: z.number().int().min(5).max(720).default(240),
  updateChannel: z.enum(['stable', 'beta']).default('stable'),
});
export type DeviceConfiguration = z.infer<typeof DeviceConfigurationSchema>;

export const SessionSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  brandProfileId: z.uuid(),
  deviceId: z.uuid(),
  status: SessionStatusSchema,
  accessMode: AccessModeSchema,
  viewerCode: z.string().regex(/^\d{6}$/),
  privateSlug: z.string().min(32),
  startedAt: z.iso.datetime(),
  liveAt: z.iso.datetime().optional(),
  endedAt: z.iso.datetime().optional(),
  expiresAt: z.iso.datetime(),
  viewerCount: z.number().int().min(0).default(0),
  peakViewerCount: z.number().int().min(0).default(0),
  ivsChannelArn: z.string().min(1),
  playbackUrl: z.url(),
  failureReason: z.string().max(500).optional(),
});
export type Session = z.infer<typeof SessionSchema>;

export const QuestionSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  viewerId: z.string().min(16).max(128),
  text: z.string().trim().min(1).max(500),
  status: QuestionStatusSchema.default('new'),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Question = z.infer<typeof QuestionSchema>;

export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  requestId: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ViewerResolutionSchema = z.object({
  sessionId: z.uuid(),
  status: SessionStatusSchema,
  brand: BrandProfileSchema,
  playbackUrl: z.url(),
  playbackToken: z.string().min(1),
  websocketToken: z.string().min(1),
  websocketUrl: z.url(),
  expiresAt: z.iso.datetime(),
  questionsEnabled: z.boolean(),
});
export type ViewerResolution = z.infer<typeof ViewerResolutionSchema>;

export const ResolveViewerSessionInputSchema = z.object({
  codeOrSlug: z.string().trim().min(6).max(128),
  pin: z
    .string()
    .regex(/^\d{4,8}$/)
    .optional(),
  viewerId: z.string().min(16).max(128),
});

export const CreateQuestionInputSchema = z.object({
  sessionId: z.uuid(),
  viewerId: z.string().min(16).max(128),
  text: z.string().trim().min(1).max(500),
});

export const DeviceEventTypeSchema = z.enum([
  'ONLINE',
  'OFFLINE',
  'STREAM_STARTING',
  'STREAM_LIVE',
  'STREAM_STOPPED',
  'STREAM_ERROR',
  'NETWORK_QUALITY',
  'OBS_ERROR',
  'MICROPHONE_ERROR',
]);

export const DeviceCommandTypeSchema = z.enum([
  'PING',
  'GET_STATUS',
  'START_STREAM',
  'STOP_STREAM',
  'RESTART_OBS',
  'SET_PROFILE',
  'SET_MICROPHONE',
  'SET_DISPLAY',
  'UPDATE_CONFIG',
  'UPDATE_AGENT',
]);

export const WebSocketEnvelopeSchema = z.object({
  id: z.uuid(),
  type: z.string().min(1).max(64),
  sentAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().optional(),
  payload: z.unknown().optional(),
});
export type WebSocketEnvelope = z.infer<typeof WebSocketEnvelopeSchema>;

export function newId(): string {
  return uuidv7();
}

export function generateViewerCode(): string {
  return randomInteger(1_000_000).toString().padStart(6, '0');
}

export function generatePrivateSlug(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return btoa(String.fromCharCode(...bytes))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '');
}

const CROCKFORD = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

export function generateEnrollmentCode(length = 8): string {
  return Array.from({ length }, () => CROCKFORD[randomInteger(CROCKFORD.length)]).join('');
}

export function normaliseEnrollmentCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function randomInteger(maxExclusive: number): number {
  const limit = Math.floor(0x1_0000_0000 / maxExclusive) * maxExclusive;
  const value = new Uint32Array(1);
  do crypto.getRandomValues(value);
  while ((value[0] ?? 0) >= limit);
  return (value[0] ?? 0) % maxExclusive;
}

export function connectionQuality(
  uploadMbps: number,
  droppedFramePercent: number,
  profile: QualityProfile,
): ConnectionQuality {
  const requiredMbps = VideoProfiles[profile].bitrateKbps / 1000;
  if (uploadMbps >= requiredMbps * 2 && droppedFramePercent < 1) return 'excellent';
  if (uploadMbps >= requiredMbps * 1.3 && droppedFramePercent < 3) return 'good';
  return 'poor';
}

const transitions: Record<SessionStatus, readonly SessionStatus[]> = {
  starting: ['live', 'stopping', 'failed'],
  live: ['stopping', 'failed'],
  stopping: ['ended', 'failed'],
  ended: [],
  failed: [],
};

export function canTransitionSession(from: SessionStatus, to: SessionStatus): boolean {
  return transitions[from].includes(to);
}

export function canManageTenant(role: Role): boolean {
  return role === 'platform_administrator' || role === 'tenant_administrator';
}

export function canManageSite(role: Role): boolean {
  return canManageTenant(role) || role === 'site_administrator';
}

export function canPresent(role: Role): boolean {
  return canManageSite(role) || role === 'presenter';
}
